import { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const UpdateNotification = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    // Listen for SW update events
    const handleSWMessage = (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        console.log('[UpdateNotification] New version:', event.data.version);
      }
    };

    // Check for waiting service worker
    const checkForUpdates = async () => {
      if (!('serviceWorker' in navigator)) return;
      
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        setShowUpdate(true);
      }
    };

    // Listen for new service worker waiting
    const handleUpdateFound = async () => {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      if (registration.waiting) {
        setShowUpdate(true);
        return;
      }

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            setShowUpdate(true);
          }
        });
      });
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    checkForUpdates();
    handleUpdateFound();

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
    };
  }, []);

  const handleUpdate = async () => {
    setUpdating(true);
    
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (error) {
      console.error('[UpdateNotification] Update error:', error);
      setUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowUpdate(false);
  };

  return (
    <AnimatePresence>
      {showUpdate && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[99999] w-[90vw] max-w-sm"
          data-testid="update-notification"
        >
          <div className="bg-gradient-to-r from-sky-500 to-cyan-500 rounded-2xl shadow-2xl shadow-sky-500/30 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <RefreshCw className={`w-5 h-5 text-white ${updating ? 'animate-spin' : ''}`} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold text-sm">Update Tersedia!</h4>
                <p className="text-white/80 text-xs mt-0.5">Versi baru dengan perbaikan & fitur terbaru</p>
              </div>
              <button
                onClick={handleDismiss}
                className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                data-testid="update-dismiss-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleDismiss}
                className="flex-1 py-2 px-4 text-white/90 text-sm font-medium rounded-xl hover:bg-white/10 transition-colors"
                data-testid="update-later-btn"
              >
                Nanti
              </button>
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="flex-1 py-2 px-4 bg-white text-sky-600 text-sm font-semibold rounded-xl hover:bg-white/90 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                data-testid="update-now-btn"
              >
                {updating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Sekarang'
                )}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default UpdateNotification;
